import React from 'react';
import {Link} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';

export const Home = () => {
    const {user} = useAuth();

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
            {/* Hero Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
                <div className="text-center">
                    <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                        <span className="block">ArtSwap</span>
                        <span className="block text-blue-600">Where Artists Connect</span>
                    </h1>
                    <p className="mt-6 max-w-2xl mx-auto text-lg text-gray-600">
                        A curated community where local artists discover and swap artwork through a fun, Tinder-like matching system. Connect with
                        fellow creatives and trade your masterpieces.
                    </p>

                    <div className="mt-8 flex justify-center space-x-4">
                        {user ? (
                            <Link
                                to="/dashboard"
                                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                            >
                                Go to Dashboard
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                                >
                                    Sign In
                                </Link>
                                <div className="text-sm text-gray-500 self-center">Registration is by invitation only</div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-extrabold text-gray-900">How It Works</h2>
                    <p className="mt-4 text-lg text-gray-600">Simple, fun, and designed for artists</p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    <div className="text-center">
                        <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload Your Art</h3>
                        <p className="text-gray-600">Share photos of your artwork with descriptions. Show off your creativity to the community.</p>
                    </div>

                    <div className="text-center">
                        <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                                />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Discover & Vote</h3>
                        <p className="text-gray-600">
                            Browse other artists' work and vote on pieces you'd love to swap with. It's like Tinder for art!
                        </p>
                    </div>

                    <div className="text-center">
                        <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 4V2a1 1 0 011-1h4a1 1 0 011 1v2h4a1 1 0 011 1v2a1 1 0 01-1 1h-1v9a2 2 0 01-2 2H6a2 2 0 01-2-2V8H3a1 1 0 01-1-1V5a1 1 0 011-1h4z"
                                />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Make the Swap</h3>
                        <p className="text-gray-600">When you both like each other's work, you get matched! Connect and arrange your art swap.</p>
                    </div>
                </div>
            </div>

            {/* Community Section */}
            <div className="bg-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-extrabold text-gray-900">Join Our Artist Community</h2>
                        <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                            ArtSwap is an invite-only community designed to foster meaningful connections between local artists. We believe in quality
                            over quantity, creating a trusted environment where artists can share their work and build lasting relationships.
                        </p>

                        <div className="mt-8 bg-blue-50 rounded-lg p-6 max-w-2xl mx-auto">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Want to Join?</h3>
                            <p className="text-gray-600 mb-4">
                                New members join by invitation only. If you're an artist interested in joining our community, ask a current member for
                                an invitation or reach out to us directly.
                            </p>
                            <div className="text-sm text-gray-500">
                                <p>✓ Curated community of local artists</p>
                                <p>✓ Secure and trusted environment</p>
                                <p>✓ Quality artwork and meaningful connections</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;
